import i18next from 'i18next';
import setGlobalState, {pushToGlobalState, unsetGlobalState} from '../setGlobalState';
import request from '../../library/request';
import store from '../../store';
import {bounceMessage} from '../../layout/userMessages/UserMessage';
import {CreateBreakPayload, CreateLecturePayload, THall, TSlot, TTimetableItem, UpdateBreakPayload, TLecturer} from './timetable.type';
import {computeMoveLecture, findLectureDay} from './lectureHelpers';
import {getStartOfDay} from '../../library/dates';
import {pickNextLectureColor} from './lectureColors';
import {addTicketToList} from '../tickets/ticketsActions';
import {wrapXHR} from '../../functions/wrapXHR';

const getEid = () => store.getState().event.eid;

const NO_DATE_KEY = 0;
const dayBucket = (l: Pick<TTimetableItem, 'tsid' | 'time'>) => l.tsid ? getStartOfDay(l.time) : NO_DATE_KEY;

export const getTimetable = async () => {
    const xhr = store.getState().timetable.getLecturesXHR;
    if(xhr === 'LOADING' || xhr === 'SUCCESS') return;

    const limit = 20;
    await wrapXHR('timetable.getLecturesXHR', async () => {
        let offset = 0;
        let fetching = true;
        while(fetching) {
            const res = await request('timetable.getTimetable', 'get', `/event/${getEid()}/timetable`, {limit, offset});
            if(res?.lectures?.length) {
                const byDay = {...store.getState().timetable.lectures};
                for(const raw of res.lectures) {
                    const lec: TTimetableItem = {...raw, time: raw.time ?? 0};
                    const dayKey = dayBucket(lec);
                    byDay[dayKey] = [...(byDay[dayKey] || []), lec];
                }
                setGlobalState('timetable.lectures', byDay);
                offset += res.lectures.length;
                fetching = res.lectures.length >= limit;
            } else {
                fetching = false;
            }
        }
    });
};

export const moveLectureToPosition = async (
    ttid: number,
    targetTsid: number,
    targetDayKey: number,
    orderedTtidsInTargetSlot: number[]
) => {
    const previousLectures = store.getState().timetable.lectures;
    const slots = store.getState().timetable.slots;
    const result = computeMoveLecture(previousLectures, slots, ttid, targetTsid, targetDayKey, orderedTtidsInTargetSlot);
    if(!result) return;
    setGlobalState('timetable.lectures', result.nextLectures);
    const res = await request('timetable.moveLectureToPosition', 'put', `/event/${getEid()}/timetablePositions`, {positions: result.positions});
    if(!res) {
        setGlobalState('timetable.lectures', previousLectures);
        return;
    }
    bounceMessage(i18next.t('timetable:lectureMoved'), 'success');
};

export const getLecturers = async () => {
    const res = await request('timetable.getLecturers', 'get', `/event/${getEid()}/lecturer`);
    if(res?.lecturers) setGlobalState('timetable.lecturers', res.lecturers);
};

export const createLecturer = async (payload: Omit<TLecturer, 'lid'>): Promise<TLecturer | null> => {
    const res = await request('timetable.createLecturer', 'post', `/event/${getEid()}/lecturer`, payload);
    if(!res?.lecturer) return null;
    pushToGlobalState('timetable.lecturers', res.lecturer);
    return res.lecturer;
};

export const getHalls = async () => {
    const res = await request('timetable.getHalls', 'get', `/event/${getEid()}/timetableHall`);
    if(res?.halls) setGlobalState('timetable.halls', res.halls);
};

export const createHall = async (title: string): Promise<THall | null> => {
    const res = await request('timetable.createHall', 'post', `/event/${getEid()}/timetableHall`, {title});
    if(!res?.hall) return null;
    pushToGlobalState('timetable.halls', res.hall);
    return res.hall;
};

export const getSlots = async () => {
    const res = await request('timetable.getSlots', 'get', `/event/${getEid()}/timetableSlot`);
    if(res?.slots) setGlobalState('timetable.slots', res.slots);
};

export const createSlot = async (timeStart: number): Promise<TSlot | null> => {
    const res = await request('timetable.createSlot', 'post', `/event/${getEid()}/timetableSlot`, {timeStart});
    if(!res?.slot) return null;
    const slots = store.getState().timetable.slots;
    if(!slots.some((s: TSlot) => s.tsid === res.slot.tsid)) {
        setGlobalState('timetable.slots', [...slots, res.slot].sort((a: TSlot, b: TSlot) => a.timeStart - b.timeStart));
    }
    return res.slot;
};

export const createLecture = async (payload: CreateLecturePayload): Promise<TTimetableItem | null> => {
    const color = pickNextLectureColor();
    const res = await request('timetable.createLecture', 'post', `/event/${getEid()}/timetable`, {...payload, color});
    if(!res?.lecture) return null;

    const lecture = {...res.lecture, time: payload.time} as TTimetableItem;
    const dayKey = dayBucket(lecture);
    const lectures = store.getState().timetable.lectures;
    const dayLectures = lectures[dayKey] || [];
    const updated = [...dayLectures, lecture].sort((a, b) => a.time - b.time);
    setGlobalState('timetable.lectures', {...lectures, [dayKey]: updated});
    if(res.casualTicket) addTicketToList(res.casualTicket);
    return lecture;
};

export const updateLecture = async (ttid: number, payload: Partial<CreateLecturePayload>): Promise<TTimetableItem | null> => {
    const res = await request('timetable.updateLecture', 'put', `/event/${getEid()}/timetable/${ttid}`, payload);
    if(!res?.lecture) return null;

    const lectures = store.getState().timetable.lectures as Record<string, TTimetableItem[]>;
    const existing = Object.values(lectures).flat().find(l => l.ttid === ttid);
    const hasDate = res.lecture.tsid;
    const lecture = {...existing, ...res.lecture, time: hasDate ? (payload.time ?? existing?.time ?? res.lecture.time) : 0} as TTimetableItem;
    const newDayKey = dayBucket(lecture);
    const oldDayKey = existing ? dayBucket(existing) : null;
    const dayUnchanged = existing && oldDayKey === newDayKey;
    const next: Record<string, TTimetableItem[]> = dayUnchanged
        ? {...lectures, [newDayKey]: lectures[newDayKey].map(l => l.ttid === ttid ? lecture : l).sort((a, b) => a.time - b.time)}
        : Object.fromEntries(Object.entries(lectures).map(([k, arr]) => [k, arr.filter(l => l.ttid !== ttid)]));
    if(!dayUnchanged) next[newDayKey] = [...(next[newDayKey] || []), lecture].sort((a, b) => a.time - b.time);
    setGlobalState('timetable.lectures', next);
    if(res.casualTicket) addTicketToList(res.casualTicket);
    bounceMessage(i18next.t('timetable:lectureUpdated'), 'success');
    return lecture;
};

export const createBreak = async (payload: CreateBreakPayload): Promise<TTimetableItem | null> => {
    const res = await request('timetable.createBreak', 'post', `/event/${getEid()}/timetable`, payload);
    if(!res?.lecture?.ttid) return null;
    const currentSlot = store.getState().timetable.slots.find((s: TSlot) => s.tsid === payload.tsid);
    const lecture: TTimetableItem = {
        ...res.lecture,
        tsid: payload.tsid,
        isBreak: true,
        time: currentSlot?.timeStart,
    };
    const dayKey = dayBucket(lecture);
    const lectures = store.getState().timetable.lectures;
    const dayLectures = lectures[dayKey] || [];
    const updated = [...dayLectures, lecture].sort((a, b) => a.time - b.time);
    setGlobalState('timetable.lectures', {...lectures, [dayKey]: updated});
    bounceMessage(i18next.t('timetable:breakCreated'), 'success');
    return lecture;
};

export const updateBreak = async (ttid: number, payload: UpdateBreakPayload): Promise<TTimetableItem | null> => {
    const res = await request('timetable.updateBreak', 'put', `/event/${getEid()}/timetable/${ttid}`, payload);
    if(!res) return null;

    const currentLectures = store.getState().timetable.lectures;
    const day = findLectureDay(currentLectures, ttid);
    if(day) {
        const [dayTs, dayLectures] = day;
        const index = dayLectures.findIndex(l => l.ttid === ttid);
        if(index !== -1) {
            setGlobalState(`timetable.lectures[${dayTs}][${index}]`, {...dayLectures[index], ...payload});
        }
    }
    bounceMessage(i18next.t('timetable:breakUpdated'), 'success');
    return res;
};

export const deleteLecture = async (ttid: number) => {
    const res = await request('timetable.deleteLecture', 'delete', `/event/${getEid()}/timetable/${ttid}`);

    if(res) {
        const currentLectures = store.getState().timetable.lectures;
        const day = findLectureDay(currentLectures, ttid);

        if(day) {
            const [dayTs, dayLectures] = day;
            const index = dayLectures.findIndex(l => l.ttid === ttid);
            if(index !== -1) unsetGlobalState(`timetable.lectures[${dayTs}][${index}]`);
        }
        bounceMessage(i18next.t('timetable:lectureDeleted'), 'success');
    }
};

export const deleteBreak = async (ttid: number): Promise<boolean> => {
    const res = await request('timetable.deleteBreak', 'delete', `/event/${getEid()}/timetable/${ttid}`);

    if(res) {
        const currentLectures = store.getState().timetable.lectures;
        const day = findLectureDay(currentLectures, ttid);

        if(day) {
            const [dayTs, dayLectures] = day;
            const index = dayLectures.findIndex(l => l.ttid === ttid);
            if(index !== -1) unsetGlobalState(`timetable.lectures[${dayTs}][${index}]`);
        }
        bounceMessage(i18next.t('timetable:breakDeleted'), 'success');
        return true;
    }
    return false;
};

export const duplicateLecture = async (ttid: number) => {
    const color = pickNextLectureColor();
    const res = await request('timetable.duplicateLecture', 'post', `/event/${getEid()}/timetable/${ttid}/duplicate`, {color});

    if(res?.lecture?.ttid) {
        const currentLectures = store.getState().timetable.lectures;
        const day = findLectureDay(currentLectures, ttid);

        if(day) {
            const [dayTs] = day;
            pushToGlobalState(`timetable.lectures[${dayTs}]`, res.lecture);
        }
        bounceMessage(i18next.t('timetable:lectureDuplicated'), 'success');
    }
};

